import React, {useMemo, useState} from 'react';
import {Button, Table, Tag} from "antd";
import UserImageCircle from "../../../components/UserImageCircle";
import Opportunity from "../../../models/Opportunity";
import SpinLoader from "../../../components/SpinLoader";
import {useTeamCandidateScope} from "../../client-lead-tools/team-scope/TeamCandidateScopeProvider";
import {useAuth} from "../../../providers/AuthProvider";
import UploadTalentContract from "../../client-lead-tools/UploadTalentContract";
import {CheckCircleOutlined, SendOutlined} from "@ant-design/icons";
import Document from '../../../models/Document'
import InfoTooltip from "../../../components/InfoTooltip";
import {ContractItem} from "../../client-lead-tools/ManageTalentContractForPosition";
import LoadingSpinner from "../../../components/LoadingSpinner";

const TeamMemberOppSummary = ({data = [], canUpload, ...props}) => {


    let totalHours = 0;
    let totalProposedHours = 0;
    let totalEstCost = 0;
    let totalProposedEstCost = 0;
    let totalPlatformCost = 0;


    data.forEach((opp) => {
        if (opp.status !== Opportunity.Statuses.Lost) {
            totalHours += TB.sumBy(opp.disciplines, discipline => {
                return discipline.total_hours
            })

            totalEstCost += TB.sumBy(opp.disciplines, discipline => {
                return discipline.payout_amount
            })
            totalPlatformCost += TB.sumBy(opp.disciplines, discipline => {
                return discipline.platform_cost
            })
        }
    });

    return (
        <Table.Summary.Row className="bg-gray-300">
            {/*<Table.Summary.Cell index={0} className="bg-gray-50">*/}

            {/*</Table.Summary.Cell>*/}
            {/*NAME*/}
            <Table.Summary.Cell index={0} className="bg-gray-50">

            </Table.Summary.Cell>
            {/*ROLES*/}
            <Table.Summary.Cell index={1} className="bg-gray-50">
                <p className="text-sm font-semibold text-right">
                    Totals
                </p>
            </Table.Summary.Cell>
            {/*TALENT HOURS*/}
            <Table.Summary.Cell index={2} className="bg-gray-50">
                <div className="h-4 flex flex-row items-center justify-start gap-1.5">
                    <p className="text-sm font-semibold whitespace-nowrap">
                        {totalHours} Hrs
                    </p>
                </div>
            </Table.Summary.Cell>


            <Table.Summary.Cell index={3} className="bg-gray-50">

            </Table.Summary.Cell>

            {/*CLIENT PAYOUT*/}
            <Table.Summary.Cell index={4} className="bg-gray-50">
                <p className="text-sm font-semibold whitespace-nowrap">
                    {TB.formatCurrency(totalPlatformCost, false)}
                </p>
            </Table.Summary.Cell>

            {/*ACTIONS*/}
            {canUpload && (
                <React.Fragment>
                    <Table.Summary.Cell index={5} className="bg-gray-50">

                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={5} className="bg-gray-50">

                    </Table.Summary.Cell>
                </React.Fragment>
            )}
        </Table.Summary.Row>
    );
}


const TYPE_TALENT_CONTRACT = 2;
const TYPE_SIGNED_TALENT_CONTRACT = 8;

const AssignedTeamSummaryTable = ({canManage, ...props}) => {

    const {
        team,
        isDisabled,
        isLoading,
        uploadTalentContract
    } = useTeamCandidateScope();

    const {auth} = useAuth();

    const canUpload = canManage && auth.canManageClients();
    const talentOpps = TB.filter(team?.job_candidate?.talent_opps || [], opp => opp.status !== Opportunity.Statuses.Lost);
    const [isUploadContractOpen, setIsUploadContractOpen] = useState(false);

    const [talentContractModel, setTalentContractModel] = useState({});

    const onCloseUploadContact = async () => {
        await setIsUploadContractOpen(false);
        setTalentContractModel({})
    }
    const columnWidths = useMemo(() => {

        return {
            Member: 200,//365,
            Roles: 320,//200,
            Talent_Hours: 90,//90,
            Client_Rate: 110,//90,
            Client_Cost: 90,
            Actions: canUpload ? 590 : 0,
        }

    }, [talentOpps, isDisabled]);
    const onRemoveFile = async (oppId,type) => {
        let confirm = await TB.sysAlert('talent.delete_proposal_slide_button_clicked');
        if (confirm) {

            return uploadTalentContract(oppId, {
                document_path: null,
                document_type: type,
                signed_contract_at: null,
                contract_available_at: null
            })
        }
    }
    const onOpenUpload = async (opp, document_type) => {
        await setTalentContractModel({opp, document_type})
        await setIsUploadContractOpen(true)
    }
    if (isLoading || !team) {
        return (
            <div className="flex flex-col justify-center items-center h-72">
                <LoadingSpinner size="lg"/>
            </div>
        )
    }
    return (
        <div className="min-h-[350px]">
            <SpinLoader isLoading={isLoading || isDisabled}>
                <Table
                    scroll={{x: TB.sum(TB.values(columnWidths))}}
                    pagination={false} rowKey='id' className="team-member-opp-status-table"
                    dataSource={talentOpps}
                    summary={(pageData) => <TeamMemberOppSummary data={pageData} canUpload={canUpload}/>}
                >

                    <Table.Column
                        title="Member"
                        dataIndex="user"
                        key="user"
                        fixed="left"
                        width={columnWidths.Member}
                        render={(user, opp) => (
                            <div key={opp.id + '-user-' + user.id}
                                 className="flex flex-row items-center gap-1.5">
                                <UserImageCircle user={user} size={32}/>
                                <p className="font-semibold text-xs">
                                    {user.fullName()}
                                </p>
                            </div>
                        )}/>
                    <Table.Column
                        title="Roles"
                        dataIndex="disciplines"
                        key="disciplines"
                        width={columnWidths.Roles}
                        render={(disciplines, opp) => (
                            <div className="flex flex-col items-start gap-2">
                                {disciplines.map((discipline) => (
                                    <div
                                        key={`${team.id}-roles-${discipline.id}`}
                                        className="h-4 flex flex-row items-center justify-start gap-1.5">
                                        <Tag color={opp.status === Opportunity.Statuses.Lost ? undefined : "purple"}
                                             key={opp.id + '-role-' + discipline.id}>
                                            {discipline.name}
                                        </Tag>
                                    </div>
                                ))}
                            </div>
                        )}
                    />
                    <Table.Column
                        title="Hours"
                        dataIndex="disciplines"
                        key="disciplines"
                        width={columnWidths.Talent_Hours}
                        render={(disciplines, opp) => (
                            <div
                                className="flex flex-col items-start gap-2">
                                {TB.map(disciplines, discipline => {
                                    // const isPending = !(opp.rejected_at || opp.withdrew_at) && discipline.matches_hourly_rate && !opp.accepted_at;

                                    // console.log('discipline.matches_hourly_rate', )
                                    return (
                                        <div key={`${opp.id}-dis-hours-${discipline.id}`}
                                             className="h-4 flex flex-row items-center justify-start gap-1.5">
                                            <p className="text-sm font-medium whitespace-nowrap">
                                                {discipline.proposed_total_periods || discipline.total_hours} Hrs
                                            </p>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    />

                    <Table.Column
                        title={<span className="text-info whitespace-no-wrap">Bill Rate</span>}
                        dataIndex="disciplines"
                        key="disciplines"
                        width={columnWidths.Client_Rate}
                        render={(disciplines, opp) => (
                            <div className="flex flex-col items-start gap-2">

                                {TB.map(disciplines, discipline => {
                                    const rate = parseFloat(discipline.proposed_hourly_rate || discipline.hourly_rate);

                                    return (
                                        <div key={`${opp.id}-dis-rate-${discipline.id}`}
                                             className="h-4 flex flex-row items-center justify-start gap-1.5">
                                            <p className="text-sm whitespace-nowrap">
                                                ${TB.formatNumber(rate * (1 + (team.job_candidate.percent_markup || 0)), 2)}/hr
                                            </p>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    />
                    <Table.Column
                        title={<span className="text-info whitespace-no-wrap">Bill Cost</span>}
                        dataIndex="disciplines"
                        key="disciplines"
                        width={columnWidths.Client_Cost}
                        render={(disciplines, opp) => {
                            const cost = TB.sumBy(disciplines, 'platform_cost');
                            return (
                                <div className="flex flex-col items-start gap-2">

                                    <div className="h-4 flex flex-row items-center justify-start gap-1.5">
                                        <p className="text-sm whitespace-nowrap">
                                            {TB.formatCurrency(cost, false)}
                                        </p>
                                    </div>
                                </div>
                            )
                        }}
                    />

                    {canUpload && (
                        <Table.ColumnGroup
                            title="Manage Talent Contract"
                            key="latest_contract"
                            fixed="right"
                        >
                            <Table.Column
                                title="Unsigned Contract"
                                dataIndex="latest_contract"
                                width={columnWidths.Actions / 2}
                                key="latest_contract"
                                render={(latest_contract, opp) => {

                                    const latestSignedContract = !!opp.latest_signed_contract && Document.make(opp.latest_signed_contract)
                                    const latestUnsignedContract = !!latest_contract && Document.make(latest_contract)
                                    const isContractAvailable = opp.contract_available_at;

                                    return (
                                        <ContractItem
                                            className="flex flex-col max-w-[265px] min-w-[265px]"
                                            file={latestUnsignedContract}
                                            empty={!latestSignedContract && (
                                                <span><i className="fas fa-exclamation-circle mr-0.5"/> Missing</span>
                                            )}
                                            isDisabled={isDisabled}
                                            canUpload={!latestSignedContract}
                                            onUpload={() => onOpenUpload(opp,TYPE_TALENT_CONTRACT)}
                                            onRemove={() => onRemoveFile(opp.id, TYPE_TALENT_CONTRACT)}
                                        >

                                            <div className="relative flex-grow mx-0 flex flex-col items-stretch">
                                                <Button
                                                    type={isContractAvailable ? undefined : 'primary'}
                                                    disabled={isDisabled || !latestUnsignedContract || isContractAvailable || latestSignedContract}
                                                    icon={isContractAvailable ? (<CheckCircleOutlined style={{fontSize: 12}}/>
                                                    ) : (
                                                        <SendOutlined style={{fontSize: 12}}/>
                                                    )}
                                                    onClick={() => uploadTalentContract(opp, {contract_available_at: new Date()})}
                                                    loading={isDisabled}
                                                    className={TB.mapClass("flex-grow mx-0", {
                                                        "cursor-default": isContractAvailable || latestSignedContract,
                                                    })}>

                                                    {!isContractAvailable ? "Make Available To Sign" : "Contract Available"}

                                                </Button>

                                                {isContractAvailable && (
                                                    <div id={`available-${opp.id}`}
                                                         className="absolute inset-0 z-[1px]"/>
                                                )}

                                            </div>
                                            {isContractAvailable && (
                                                <InfoTooltip type="dark" placement="top"
                                                             target={`available-${opp.id}`}>
                                                    <p className="text-white text-xs px-1.5 py-0.5">
                                                        Made available for talent to download and
                                                        sign: {TB.moment(isContractAvailable)
                                                        .format('lll')}
                                                    </p>
                                                </InfoTooltip>
                                            )}


                                        </ContractItem>
                                    )
                                }}
                            />
                            <Table.Column
                                title="Signed Contract"
                                dataIndex="latest_signed_contract"
                                width={columnWidths.Actions / 2}
                                key="latest_signed_contract"
                                render={(latest_signed_contract, opp) => {
                                    const latestSignedContract = !!latest_signed_contract && Document.make(latest_signed_contract)
                                    const latestUnsignedContract = !!opp.latest_contract && Document.make(opp.latest_contract)
                                    const isContractAvailable = opp.contract_available_at;

                                    return (
                                        <ContractItem
                                            className="flex flex-col max-w-[250px] min-w-[250px]"
                                            file={latestSignedContract}
                                            empty={latestUnsignedContract ? (
                                                isContractAvailable ?
                                                    <span><i className="fas fa-history mr-0.5"/> Waiting for Signature</span> :
                                                    <span><i className="fas fa-info-circle mr-0.5"/> Ready, Not Sent</span>
                                            ) : (
                                                <span><i className="fas fa-exclamation-circle mr-0.5"/> Missing</span>
                                            )}
                                            isDisabled={isDisabled}
                                            canUpload={!latestUnsignedContract}
                                            onUpload={() => onOpenUpload(opp, TYPE_SIGNED_TALENT_CONTRACT)}
                                            onRemove={() => onRemoveFile(opp.id, TYPE_SIGNED_TALENT_CONTRACT)}
                                        />
                                    )
                                }}
                            />
                        </Table.ColumnGroup>
                    )}
                </Table>

            </SpinLoader>

            {canUpload && (

                <UploadTalentContract
                    isDisabled={isDisabled}
                    title={`Manage Team Member Contract: ${talentContractModel.opp ? talentContractModel.opp.user?.fullName?.() : ''}`}
                    setFormModel={setTalentContractModel}
                    formModel={talentContractModel}
                    onUpload={async () => {
                        let {
                            opp,
                            ...params
                        } = talentContractModel;

                        await uploadTalentContract(oppId, params);
                    }}
                    isOpen={isUploadContractOpen}
                    onClose={onCloseUploadContact}
                />
            )}
        </div>
    )
}

AssignedTeamSummaryTable.propTypes = {};

export default AssignedTeamSummaryTable;